import * as React from 'react';

import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';

import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { formatDate } from '@/lib/dateFormatter';
import { exportCsvSingleFilePapa, filterCsvDataByVisibleColumns } from '@/lib/exportCsv';
import { formatAmount } from '@/lib/format';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { AffiliateData } from '@/services/payment/payment.types';
import { getColumns } from './Column';

export function AffiliateLogTable({
  data = [],
  pagination,
  loading,
}: {
  data?: AffiliateData[];
  pagination: Pagination;
  loading: boolean;
}) {
  const { t } = useDynamicLocale();
  const columns = React.useMemo(() => getColumns(t), [t]);

  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const EXPORT_KEYS_BY_COLUMN: Record<string, readonly string[]> = {
    sn: ['SN'],
    monthandyear: [t('common.page.tableColumn.monthAndYear')],
    withdrawDay: [t('common.page.tableColumn.withdrawDay')],
    totalRequestedAmount: [t('common.page.tableColumn.requestedAmount')],
  };

  const handleExport = () => {
    const exportData = table.getFilteredRowModel().rows.map(({ original: row, index }) => ({
      SN: getSerialNumber(table, index),
      [t('common.page.tableColumn.monthAndYear')]: formatDate(row.monthandyear, 'MMM YYYY'),
      [t('common.page.tableColumn.withdrawDay')]: row.withdrawDay,
      [t('common.page.tableColumn.requestedAmount')]: formatAmount(row.totalRequestedAmount),
    }));

    const visibleColumnIds = table.getVisibleLeafColumns().map((column) => column.id);
    const visibleData = filterCsvDataByVisibleColumns(exportData, visibleColumnIds, EXPORT_KEYS_BY_COLUMN);

    exportCsvSingleFilePapa(visibleData, {
      fileName: 'affiliate-withdrawal-logs-report',
      format: 'YYYY/MM/DD HH:mm:ss',
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount,
    initialState: {
      sorting: [{ id: 'sn', desc: false }],
      columnVisibility: {
        createdDate: false,
      },
    },
    meta: {
      title: <Translation id="paymentManagement.affiliatePaymentLogs.table.title" defaultMessage="Withdrawal Logs" />,
      totalPage: pagination.totalItems,
      loading,
      actionKey: 'exportCsv',
      action: handleExport,
    },
    getRowId: (originalRow: AffiliateData) => String(originalRow.id),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <DataTable table={table}>
      <DataTableToolbar dateFilterFirst={true} hideViewOptions />
    </DataTable>
  );
}
